import React from 'react';
import { Box, Stack, Tooltip } from '@mui/material';
import type { StagedFieldSource, StagedValueSource } from '../utils/stagedFieldSource';
import { tokens } from '../theme/tokens';

const LABELS: Record<StagedValueSource, string> = {
  scraped: 'Vendor',
  official: 'API',
  '3p': '3P',
  ai: 'AI',
  manual: 'Manual',
  motzu: 'Motzu',
  missing: 'Missing',
};

const HINTS: Record<StagedValueSource, string> = {
  scraped: 'Manufacturer store scrape',
  official: 'Official vendor API or MAP / pricing ingest',
  '3p': 'Third-party fill',
  ai: 'AI generated or AI-confirmed',
  manual: 'Edited by an admin',
  motzu: 'Motzu taxonomy — rules and catalog vocabulary, not vendor store tags',
  missing: 'No value on this field',
};

/**
 * Filled square-ish chips, one tone per source so the seven read apart at a glance:
 * scrape info-blue, official positive-green, 3P caution-amber, AI accent tint with
 * neutral ink, manual neutral grey, Motzu the bolder accent blue, missing problem-red.
 */
const TONES: Record<StagedValueSource, { bg: string; fg: string; border: string }> = {
  scraped: { bg: tokens.signal.info.tint, fg: tokens.signal.info.ink, border: tokens.signal.info.border },
  official: { bg: tokens.signal.positive.tint, fg: tokens.signal.positive.ink, border: tokens.signal.positive.border },
  '3p': { bg: tokens.signal.caution.tint, fg: tokens.signal.caution.ink, border: tokens.signal.caution.border },
  ai: { bg: tokens.accent.tint, fg: tokens.ink.primary, border: tokens.accent.border },
  manual: { bg: tokens.signal.neutral.tint, fg: tokens.signal.neutral.ink, border: tokens.signal.neutral.border },
  // Motzu's own taxonomy tag: the same accent family as `ai`/`scraped`, but the
  // stronger tint and a solid accent border keep it visually bolder than either.
  motzu: { bg: tokens.accent.tintStrong, fg: tokens.accent.main, border: tokens.accent.main },
  missing: { bg: tokens.signal.problem.tint, fg: tokens.signal.problem.ink, border: tokens.signal.problem.border },
};

function chipLabel(source: StagedFieldSource): string {
  if (source.detail === 'upc_based') return 'UPC based';
  if (source.detail === 'barcodelookup') return 'Barcode Lookup';
  if (source.source === 'official' && /map|pricing_import/i.test(source.detail || '')) {
    return 'MAP';
  }
  const conf = source.source === 'ai' && source.confidence != null ? ` ${source.confidence}%` : '';
  return `${LABELS[source.source]}${conf}`;
}

function chipHint(source: StagedFieldSource): string {
  if (source.detail === 'upc_based') {
    return 'Motzu presumed this from the vendor UPC using GS1 leading-zero padding. The source did not publish a separate EAN/GTIN.';
  }
  if (source.detail === 'barcodelookup') {
    return 'Barcode Lookup, matched on this product\'s UPC. Retailer prices from that site are not used.';
  }
  return HINTS[source.source];
}

export const StagedSourceBadge: React.FC<{
  source?: StagedFieldSource | null;
  /** Overrides the chip text (e.g. "Price sheet") while keeping the source's tone and hint. */
  label?: string;
}> = ({ source, label }) => {
  if (!source) return null;
  const tone = TONES[source.source];
  const title = [
    chipHint(source),
    source.detail === 'upc_based' || source.detail === 'barcodelookup' ? '' : source.detail,
    source.confidence != null ? `${source.confidence}% confidence` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Tooltip
      title={title}
      placement="top"
      enterDelay={250}
      leaveDelay={0}
      disableInteractive
      describeChild
      slotProps={{
        popper: {
          modifiers: [
            { name: 'offset', options: { offset: [0, 6] } },
            { name: 'preventOverflow', options: { padding: 8 } },
          ],
        },
        tooltip: {
          sx: {
            maxWidth: 260,
            fontSize: '0.7rem',
            lineHeight: 1.35,
            fontWeight: 500,
          },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          height: 18,
          px: 0.6,
          borderRadius: '4px',
          border: `1px solid ${tone.border}`,
          bgcolor: tone.bg,
          color: tone.fg,
          fontSize: '0.58rem',
          fontWeight: 700,
          letterSpacing: '0.03em',
          lineHeight: 1,
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        {label ?? chipLabel(source)}
      </Box>
    </Tooltip>
  );
};

export const SectionTitleWithSource: React.FC<{
  title: React.ReactNode;
  extra?: React.ReactNode;
  source?: StagedFieldSource | StagedFieldSource[] | null;
}> = ({ title, extra, source }) => {
  const sources = Array.isArray(source) ? source : source ? [source] : [];
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} component="span" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      <span>{title}</span>
      {extra}
      {sources.map((item, index) => (
        <StagedSourceBadge key={`${item.source}-${item.detail || index}`} source={item} />
      ))}
    </Stack>
  );
};
